import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  CellRenderer,
  createMasonryCellPositioner,
  Masonry,
  OnScrollCallback
} from 'react-virtualized';
import { ISearchInputProps } from '@picsart/rc/types/components/Search/types';
import { useScrollbars } from '@picsart/rc/editor/Scrollable';
import { useTranslator } from '@picsart/rc/services/localization';
import Search from '@picsart/rc/old/Search';
import Card from '@picsart/rc/editor/Card';
import VideoImage from '@picsart/rc/editor/VideoImage';
import Skeleton from '@picsart/rc/old/Skeleton';
import { StockVideo } from 'types/StockVideo';
import { noop } from 'utils/noop';
import { StockVideosToolProps } from './types';
import CommercialDropdown from '@picsart/rc/old/CommercialDropdown';
import NoResult from '@picsart/rc/old/NoResult';

const DEFAULT_WIDTH = 154;
const DEFAULT_HEIGHT = 86;

const createCellRenderer = (
  cellMeasurerCache: CellMeasurerCache,
  videos: StockVideo[],
  handleSelectVideo: (id: string) => void,
  premiumMessage: string
): CellRenderer => ({ index, style, parent, key }) => {
  if (index > videos.length - 1) return;
  const item: StockVideo = videos[index];

  const size = item?.previewSize ?? { height: DEFAULT_HEIGHT, width: DEFAULT_WIDTH };
  const height = Math.ceil(DEFAULT_WIDTH * (size.height / size.width) || DEFAULT_HEIGHT) + 8;

  return (
    <CellMeasurer cache={cellMeasurerCache} key={key} parent={parent} index={index}>
      <div style={style}>
        <div style={{ height, width: DEFAULT_WIDTH }}>
          <Card
            isPremium={item && item.license !== 'fte'}
            isFull
            onClick={item ? () => handleSelectVideo(videos[index].id) : noop}
            premiumMessage={premiumMessage}
          >
            {item ? (
              <VideoImage
                isMuteBtnExist={false}
                muted
                loop
                imageSrc={item.thumbnail_url}
                videoSrc={item.preview_url}
              />
            ) : (
              <div sx={{ width: DEFAULT_WIDTH, height }}>
                <Skeleton />
              </div>
            )}
          </Card>
        </div>
      </div>
    </CellMeasurer>
  );
};

export const StockVideoToolBase: FC<StockVideosToolProps> = ({
  videos,
  selectVideo,
  setQuery,
  query,
  requestNextPage,
  loading,
  isCommercial,
  setIsCommercial
}) => {
  const { scrollbars } = useScrollbars();
  const masonryRef = useRef<any>();
  const translate = useTranslator();

  const cellMeasurerCache = useMemo(
    () =>
      new CellMeasurerCache({
        defaultHeight: DEFAULT_HEIGHT,
        defaultWidth: DEFAULT_WIDTH,
        fixedWidth: true
      }),
    []
  );

  const cellPositionerConfig = useMemo(
    () => ({
      cellMeasurerCache,
      columnCount: 2,
      columnWidth: DEFAULT_WIDTH,
      spacer: 10
    }),
    [cellMeasurerCache]
  );

  const cellPositioner = useMemo(() => createMasonryCellPositioner(cellPositionerConfig), [
    cellPositionerConfig
  ]);

  const handleContainerScroll = useCallback<OnScrollCallback>(
    ({ scrollTop, scrollHeight, clientHeight }) => {
      if (!loading && scrollHeight === clientHeight + scrollTop) {
        requestNextPage();
      }
    },
    [requestNextPage, loading]
  );

  const handleSearchChange: ISearchInputProps['onChange'] = useCallback(
    e => {
      setQuery(e.target.value);
    },
    [setQuery]
  );

  const handleCommercialChange = useCallback(
    (_, value) => {
      setIsCommercial(!!value);
    },
    [setIsCommercial]
  );

  const videosWithPlaceholders = useMemo(() => {
    const placeholders = new Array(40).fill(null);
    return loading ? [...videos, ...placeholders] : videos;
  }, [videos, loading]);

  useEffect(() => {
    cellMeasurerCache.clearAll();
    cellPositioner.reset(cellPositionerConfig);
    masonryRef.current?.clearCellPositions();
  }, [videosWithPlaceholders, cellMeasurerCache]);

  const cellRenderer: CellRenderer = useMemo(
    () =>
      createCellRenderer(
        cellMeasurerCache,
        videosWithPlaceholders,
        selectVideo,
        translate('This is a premium video')
      ),
    [videosWithPlaceholders, selectVideo, cellMeasurerCache]
  );

  return (
    <div sx={{ '& *': { boxSizing: 'border-box' }, px: '10px', height: '100%' }}>
      <div
        sx={{
          margin: '15px 10px 17px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end'
        }}
      >
        <Search value={query} onChange={handleSearchChange} placeholder={translate('Search')} />
        <CommercialDropdown onChange={handleCommercialChange} value={Number(isCommercial)} />
      </div>
      <div sx={{ height: 'calc(100vh - 284px)' }}>
        {!loading && videos?.length === 0 ? (
          <div sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', height: '100%' }}>
            <NoResult searchValue={query} />
          </div>
        ) : (
          <AutoSizer>
            {({ width, height }) => (
              <Masonry
                ref={masonryRef}
                className={scrollbars}
                height={height}
                width={width}
                cellMeasurerCache={cellMeasurerCache}
                cellCount={videosWithPlaceholders.length}
                cellRenderer={cellRenderer}
                cellPositioner={cellPositioner}
                autoHeight={false}
                onScroll={handleContainerScroll}
              />
            )}
          </AutoSizer>
        )}
      </div>
    </div>
  );
};
